import React, { useReducer, useMemo } from 'react'
import { List, ListItem, Button, Paragraph, FlowGrid, useOverlay } from '@fs/zion-ui'
import { useSlsDispatchContext, getNestedSubelementsByType } from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'
import { useSelectedPerson } from '../../../../../shared/hooks/useSelectedPerson'
import { EssentialInformationCard } from '../../../../components/generic/essentialInformationCard'
import { FamilyMembersCard } from '../../../../components/generic/familyMembersCard/FamilyMembersCard'
import EventsCard from '../../../../components/generic/eventsCard/EventsCard'
import AdditionalFactsCard from '../../../../components/generic/additionalFactsCard/AdditionalFactsCard'
import TreeAttachmentCard from '../../../../components/generic/treeAttachmentCard/TreeAttachmentCard'
import { DeleteOverlay } from '../overlays'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import ShowChangeHistory from '../ui/ShowChangeHistory'
import { stepperEditViewInitialState, stepperReducer, STEPS } from '../../../../utils/stepperReducer'
import { useDisplayHighlights } from '../../../../hooks/useDisplayHighlights'

/**
 * PersonOverviewV2 - A comprehensive view component for displaying and managing a selected person's information
 * in the Record Image Processor (RIP) indexing interface.
 *
 * This component provides a complete overview of a person extracted from a historical record, including their
 * essential information, family relationships, events, additional facts, and tree attachment status. It allows
 * users to view the person's record, edit information through various cards, access change history, and delete
 * the person if needed.
 *
 * The component displays:
 * - Person's name and relationship to the primary person
 * - Link to view the full record
 * - Tree attachment status
 * - Essential information card (names, gender, etc.)
 * - Family members card (relationships)
 * - Events card (birth, death, etc.)
 * - Additional facts card
 * - Change history
 * - Delete person functionality
 *
 * @param {Object} props - Component props
 * @param {Function} props.handleDeletePerson - Callback function to handle person deletion. Receives the person ID as a parameter.
 * @param {Function} props.onDirtyChange - Callback function that is called when the dirty state of any card changes.
 * Receives a boolean indicating whether there are unsaved changes.
 *
 * @returns {React.ReactElement} The rendered PersonOverviewV2 component
 *
 * @example
 * <PersonOverviewV2 handleDeletePerson={(personId) => deletePerson(personId)} onDirtyChange={(isDirty) => console.log(isDirty)} />
 */
const PersonOverviewV2 = ({ handleDeletePerson, onDirtyChange = () => {} }) => {
  const [t] = useTranslation()
  const { selectedPerson, selectedPersonName, relationshipText, currentImage } = useSelectedPerson()
  const { closeAll } = useOverlayContext()
  const [stepperState, stepperDispatch] = useReducer(stepperReducer, stepperEditViewInitialState)
  const zionSlsDispatch = useSlsDispatchContext()
  const deletePersonOverlay = useOverlay()

  const handleSubmit = async (currentStep) => {
    // Clear out the localizedNames value to trigger the hook to fetch it again.
    zionSlsDispatch({
      type: 'SET_PERSON_LOCALIZED_NAME',
      value: {
        personId: selectedPerson?.id,
        localizedName: '',
        partitionKey: currentImage?.partitionKey,
      },
    })
    stepperDispatch({ type: 'SET_EDIT_MODE', step: currentStep, value: false, returnToPreviousStep: true })

    // Reset dirty state after successful save to prevent unsaved changes dialog
    onDirtyChange(false)
  }

  // Set highlights for all person fields when all cards are in view mode (closed)
  const allCardsClosed = useMemo(
    () => Object.values(stepperState.editMode).every((isOpen) => !isOpen),
    [stepperState.editMode]
  )

  const fieldIds = useMemo(() => {
    if (!selectedPerson) return []
    const fields = getNestedSubelementsByType(selectedPerson, 'FIELD')
    return fields.map((field) => field.id).filter(Boolean)
  }, [selectedPerson])

  useDisplayHighlights({
    elementIds: fieldIds,
    enabled: allCardsClosed && !!selectedPerson && fieldIds.length > 0,
  })

  return (
    <FlowGrid columnCount={1} guttersY="xxs">
      <List>
        <ListItem dense emphasized overline={relationshipText} primaryText={selectedPersonName} />
      </List>
      <Button
        dense
        emphasis="low"
        data-testid="person-overview-v2-view-record-button"
        to={`/ark:/61903/${selectedPerson?.id}`}
        target="_blank"
        external
      >
        {t('person-overview-v2.view-record', 'View Record')}
      </Button>
      <Paragraph size="sm">
        {t('person-overview-v2.instruction', 'Click any edit button to fix the text or highlights')}
      </Paragraph>
      <TreeAttachmentCard ark={`/ark:/61903/${selectedPerson?.id}`} />
      <EssentialInformationCard
        person={selectedPerson}
        reviewed
        onSave={() => handleSubmit(STEPS.ESSENTIAL_INFO)}
        editMode={stepperState.editMode[STEPS.ESSENTIAL_INFO]}
        setEditMode={(value) =>
          stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.ESSENTIAL_INFO, value, returnToPreviousStep: true })
        }
        onDirtyChange={onDirtyChange}
        saveCardDataToSls
      />
      <FamilyMembersCard
        person={selectedPerson}
        reviewed
        editMode={stepperState.editMode[STEPS.FAMILY_MEMBERS]}
        setEditMode={(value) =>
          stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.FAMILY_MEMBERS, value, returnToPreviousStep: true })
        }
        onSave={() => handleSubmit(STEPS.FAMILY_MEMBERS)}
        onDirtyChange={onDirtyChange}
        saveCardDataToSls
      />
      <EventsCard
        person={selectedPerson}
        reviewed
        editMode={stepperState.editMode[STEPS.EVENTS]}
        setEditMode={(value) =>
          stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.EVENTS, value, returnToPreviousStep: true })
        }
        onSave={() => handleSubmit(STEPS.EVENTS)}
        onDirtyChange={onDirtyChange}
        saveCardDataToSls
      />
      <AdditionalFactsCard
        person={selectedPerson}
        reviewed
        editMode={stepperState.editMode[STEPS.ADDITIONAL_FACTS]}
        setEditMode={(value) =>
          stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.ADDITIONAL_FACTS, value, returnToPreviousStep: true })
        }
        onSave={() => handleSubmit(STEPS.ADDITIONAL_FACTS)}
        onDirtyChange={onDirtyChange}
        saveCardDataToSls
      />
      <ShowChangeHistory showIcon={false} />
      <Button
        dense
        emphasis="low"
        color="danger"
        data-testid="person-overview-v2-delete-person-button"
        onClick={() => {
          deletePersonOverlay.handleClick()
        }}
      >
        {t('person-overview-v2.delete-person', 'Delete {selectedPersonName}', { selectedPersonName })}
      </Button>
      <DeleteOverlay
        overlay={deletePersonOverlay}
        headingText={t('remove-person.delete-individual-overlay.heading-text', `Delete {name}`, {
          name: selectedPersonName,
        })}
        handleDelete={() => {
          handleDeletePerson(selectedPerson?.id)
          closeAll()
        }}
      >
        <>
          {t(
            'remove-person.delete-individual-overlay.content-text',
            `Deleting {name} will remove this person and all of their content. Undo this action through this individual's Change History in the record.`,
            { name: selectedPersonName }
          )}
        </>
      </DeleteOverlay>
    </FlowGrid>
  )
}

export default PersonOverviewV2
